import Q from 'q';
import request from 'superagent';
import ACTION_TYPES from '../../constants/actionTypes';
import { showToast } from './misc';
import isEmpty from 'lodash/isEmpty';
import {
  createStripeTokenFromElement,
  updatePaymentMethod,
  getErrorMessage,
  setSentryUserContext,
} from 'shared/lib/utils';
import * as analytics from 'shared/lib/analytics';
import api from '../../api';

/*
 * Account info
 * ========================================
 */

function getAccountInfoRequest() {
  return {
    type: ACTION_TYPES.GET_ACCOUNT_INFO,
  };
}

function getAccountInfoSuccess(data) {
  return {
    type: ACTION_TYPES.GET_ACCOUNT_INFO_SUCCESS,
    payload: data,
  };
}

function getAccountInfoFailure(error) {
  return {
    type: ACTION_TYPES.GET_ACCOUNT_INFO_FAILURE,
    error,
  };
}

export function getAccountInfo() {
  return async (dispatch, getState) => {
    const deferred = Q.defer();
    if (isEmpty(getState().account)) {
      dispatch(getAccountInfoRequest());

      try {
        var myHeaders = new Headers();
        myHeaders.append('Authorization', `Bearer ${getState().authToken}`);

        var requestOptions = {
          headers: myHeaders,
        };

        const res = await fetch(
          `${
            process.env.REACT_APP_SURE_API_BASE_URL
          }/api/user/v2/accounts/info`,
          requestOptions
        );

        const data = await res.json();
        if (data.error) {
          dispatch(getAccountInfoFailure(data.error.message));
          deferred.reject(data.error.message);
          dispatch(showToast(data.error.message, 'error'));
        } else {
          dispatch(getAccountInfoSuccess(data));
          deferred.resolve(data);
        }
      } catch (err) {
        console.log({ err });
        const errMessage = getErrorMessage(err);
        dispatch(getAccountInfoFailure(errMessage));
        deferred.reject(errMessage);
        dispatch(showToast(errMessage, 'error'));
      }
    } else {
      deferred.resolve();
    }

    return deferred.promise;
  };
}

function updateAccountInfoRequest() {
  return {
    type: ACTION_TYPES.UPDATE_ACCOUNT_INFO,
  };
}

function updateAccountInfoSuccess(data) {
  setSentryUserContext(data.account);
  analytics.identify(data.account);
  analytics.track('Portal - Account Save Success', { category: 'user' });
  return {
    type: ACTION_TYPES.UPDATE_ACCOUNT_INFO_SUCCESS,
    payload: data,
  };
}

function updateAccountInfoFailure(error) {
  analytics.track('Portal - Account Save Failure', { category: 'user' });
  return {
    type: ACTION_TYPES.UPDATE_ACCOUNT_INFO_FAILURE,
    error,
  };
}

export function updateAccountInfo(accountData) {
  return (dispatch, getState) => {
    const deferred = Q.defer();

    accountData.authToken = getState().authToken;

    dispatch(updateAccountInfoRequest());
    api
      .updateAccountInfo(accountData)
      .then(updatedData => {
        dispatch(updateAccountInfoSuccess(updatedData));
        deferred.resolve(updatedData);
      })
      .catch(error => {
        const errMessage = getErrorMessage(error);
        dispatch(updateAccountInfoFailure(errMessage));
        deferred.reject(errMessage);
      });

    return deferred.promise;
  };
}

/*
 * Payment methods
 * ========================================
 */

function addPaymentMethodRequest() {
  return {
    type: ACTION_TYPES.ADD_PAYMENT_METHOD_REQUEST,
  };
}

function addPaymentMethodSuccess(paymentMethod) {
  return {
    type: ACTION_TYPES.ADD_PAYMENT_METHOD_SUCCESS,
    paymentMethod,
  };
}

function addPaymentMethodFailure(error) {
  return {
    type: ACTION_TYPES.ADD_PAYMENT_METHOD_FAILURE,
    error,
  };
}

export function addPaymentMethod(cardElement) {
  return (dispatch, getState) => {
    const deferred = Q.defer();

    dispatch(addPaymentMethodRequest());
    createStripeTokenFromElement(cardElement)
      .then(stripeToken => {
        const { email, firstName, lastName } = getState().account;

        return updatePaymentMethod({
          authToken: getState().authToken,
          apiEndpoint: `${
            process.env.REACT_APP_API_V2
          }/api/v2/policies/update-all-payment-methods/`,
          email,
          stripeToken,
          name: `${firstName} ${lastName}`,
        });
      })
      .then(paymentMethod => {
        dispatch(addPaymentMethodSuccess(paymentMethod));
        deferred.resolve(paymentMethod);
      })
      .catch(error => {
        const errMessage = getErrorMessage(error);
        dispatch(addPaymentMethodFailure(errMessage));
        deferred.reject(errMessage);
        dispatch(showToast(errMessage, 'error'));
      });

    return deferred.promise;
  };
}

function deletePaymentMethodRequest(paymentMethodId) {
  return {
    type: ACTION_TYPES.DELETE_PAYMENT_METHOD_REQUEST,
    paymentMethodId,
  };
}

function deletePaymentMethodSuccess() {
  return {
    type: ACTION_TYPES.DELETE_PAYMENT_METHOD_SUCCESS,
  };
}

function deletePaymentMethodFailure(error) {
  return {
    type: ACTION_TYPES.DELETE_PAYMENT_METHOD_FAILURE,
    error,
  };
}

export function deletePaymentMethod(paymentMethodId) {
  return (dispatch, getState) => {
    const deferred = Q.defer();

    dispatch(deletePaymentMethodRequest(paymentMethodId));
    request
      .post(
        `${
          process.env.REACT_APP_SURE_API_BASE_URL
        }/api/user/v2/accounts/payment-method/remove`
      )
      .set('Authorization', `Bearer ${getState().authToken}`)
      .send({
        payment_method_id: paymentMethodId,
      })
      .end((err, response) => {
        if (err) {
          const errMessage = getErrorMessage(err);
          dispatch(deletePaymentMethodFailure(errMessage));
          deferred.reject(errMessage);
          dispatch(showToast(errMessage, 'error'));
        } else {
          dispatch(deletePaymentMethodSuccess());
          deferred.resolve();
        }
      });

    return deferred.promise;
  };
}

/*
 * Bank account
 * ========================================
 */

function saveBankAccountRequest() {
  analytics.track('Portal - Claim Bank Account Save', {
    category: 'claim',
  });
  return {
    type: ACTION_TYPES.SAVE_BANK_ACCOUNT,
  };
}

function saveBankAccountSuccess(data) {
  analytics.track('Portal - Claim Bank Account Save Success', {
    category: 'claim',
  });
  return {
    type: ACTION_TYPES.SAVE_BANK_ACCOUNT_SUCCESS,
    data,
  };
}

function saveBankAccountFailure(error) {
  analytics.track('Portal - Claim Bank Account Save Failure', {
    category: 'claim',
  });
  return {
    type: ACTION_TYPES.SAVE_BANK_ACCOUNT_FAILURE,
    error,
  };
}

export function saveBankAccount(accountDetails) {
  return (dispatch, getState) => {
    const deferred = Q.defer();

    dispatch(saveBankAccountRequest());
    request
      .post(
        `${
          process.env.REACT_APP_SURE_API_BASE_URL
        }/api/user/v2/accounts/bank-account`
      )
      .send(accountDetails)
      .set('Authorization', `Bearer ${getState().authToken}`)
      .end((err, response) => {
        if (err) {
          const errMessage = getErrorMessage(err);
          dispatch(saveBankAccountFailure(errMessage));
          deferred.reject(errMessage);
          dispatch(showToast(errMessage, 'error'));
        } else {
          dispatch(saveBankAccountSuccess(response.body));
          deferred.resolve(response.body);
        }
      });

    return deferred.promise;
  };
}
